import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, DollarSignIcon, BuildingIcon, CalendarIcon, FileTextIcon, CheckCircleIcon } from 'lucide-react';
export function BillFinancingWizard() {
  const navigate = useNavigate();
  const {
    id
  } = useParams();
  const [step, setStep] = useState(1);
  const [formData, setFormData] = useState({
    loanPackage: '',
    financingAmount: '',
    requestedTerm: '30',
    purpose: '',
    additionalNotes: ''
  });
  // Mock bill data
  const bill = {
    id: id,
    billNumber: 'BILL-2024-002',
    supplierName: 'Spice Traders Co',
    amount: 87500,
    dueDate: '2024-02-20'
  };
  const dfccLoanPackages = [{
    id: 'standard',
    name: 'Standard Financing',
    interestRate: '12%',
    maxTerm: '90 days',
    processingTime: '48 hours',
    description: 'Ideal for regular business operations with flexible terms'
  }, {
    id: 'premium',
    name: 'Premium Financing',
    interestRate: '10.5%',
    maxTerm: '120 days',
    processingTime: '24 hours',
    description: 'Lower rates for established businesses with priority processing'
  }, {
    id: 'express',
    name: 'Express Financing',
    interestRate: '14%',
    maxTerm: '60 days',
    processingTime: '12 hours',
    description: 'Quick approval for urgent cash flow needs'
  }, {
    id: 'sme',
    name: 'SME Growth Package',
    interestRate: '11%',
    maxTerm: '90 days',
    processingTime: '36 hours',
    description: 'Special rates for small and medium enterprises'
  }];
  const handleSubmit = () => {
    console.log('Submitting financing request:', {
      billId: id,
      ...formData
    });
    navigate('/bills');
  };
  const selectedPackage = dfccLoanPackages.find(p => p.id === formData.loanPackage);
  return <div className="max-w-4xl mx-auto space-y-6" data-id="element-2679">
      {/* Header */}
      <div className="flex items-center justify-between" data-id="element-2680">
        <button onClick={() => navigate(`/bill-detail/${id}`)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900" data-id="element-2681">
          <ArrowLeftIcon size={20} data-id="element-2682" />
          Back to Bill
        </button>
      </div>
      {/* Progress Steps */}
      <div className="bg-white rounded-lg border border-gray-200 p-6" data-id="element-2683">
        <div className="flex items-center justify-between mb-8" data-id="element-2684">
          <div className="flex-1" data-id="element-2685">
            <div className={`flex items-center ${step >= 1 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2686">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 1 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2687">
                1
              </div>
              <div className="ml-3" data-id="element-2688">
                <div className="text-sm font-medium" data-id="element-2689">Bill Details</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 2 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2690"></div>
          <div className="flex-1" data-id="element-2691">
            <div className={`flex items-center ${step >= 2 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2692">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 2 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2693">
                2
              </div>
              <div className="ml-3" data-id="element-2694">
                <div className="text-sm font-medium" data-id="element-2695">Select Provider</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 3 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2696"></div>
          <div className="flex-1" data-id="element-2697">
            <div className={`flex items-center ${step >= 3 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2698">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 3 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2699">
                3
              </div>
              <div className="ml-3" data-id="element-2700">
                <div className="text-sm font-medium" data-id="element-2701">Financing Terms</div>
              </div>
            </div>
          </div>
          <div className={`flex-1 h-1 ${step >= 4 ? 'bg-blue-600' : 'bg-gray-200'}`} data-id="element-2702"></div>
          <div className="flex-1" data-id="element-2703">
            <div className={`flex items-center ${step >= 4 ? 'text-blue-600' : 'text-gray-400'}`} data-id="element-2704">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${step >= 4 ? 'bg-blue-600 text-white' : 'bg-gray-200'}`} data-id="element-2705">
                4
              </div>
              <div className="ml-3" data-id="element-2706">
                <div className="text-sm font-medium" data-id="element-2707">Review & Submit</div>
              </div>
            </div>
          </div>
        </div>
        {/* Step 1: Bill Details */}
        {step === 1 && <div className="space-y-6" data-id="element-2708">
            <div data-id="element-2709">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2710">
                Bill Information
              </h2>
              <p className="text-gray-600" data-id="element-2711">
                Review the bill details for financing
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-4" data-id="element-2712">
              <div className="grid grid-cols-2 gap-4" data-id="element-2713">
                <div data-id="element-2714">
                  <div className="text-sm text-gray-500" data-id="element-2715">Bill Number</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2716">
                    {bill.billNumber}
                  </div>
                </div>
                <div data-id="element-2717">
                  <div className="text-sm text-gray-500" data-id="element-2718">Supplier</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2719">
                    {bill.supplierName}
                  </div>
                </div>
                <div data-id="element-2720">
                  <div className="text-sm text-gray-500" data-id="element-2721">Due Date</div>
                  <div className="text-base font-medium text-gray-900" data-id="element-2722">
                    {new Date(bill.dueDate).toLocaleDateString('en-GB')}
                  </div>
                </div>
                <div data-id="element-2723">
                  <div className="text-sm text-gray-500" data-id="element-2724">Bill Amount</div>
                  <div className="text-2xl font-bold text-gray-900" data-id="element-2725">
                    Rs. {bill.amount.toLocaleString('en-LK')}
                  </div>
                </div>
              </div>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2726">
              <div className="flex items-start gap-3" data-id="element-2727">
                <DollarSignIcon size={20} className="text-blue-600 mt-0.5" data-id="element-2728" />
                <div className="text-sm text-blue-800" data-id="element-2729">
                  <div className="font-medium mb-1" data-id="element-2730">
                    Bill Financing Benefits
                  </div>
                  <ul className="list-disc list-inside space-y-1" data-id="element-2731">
                    <li data-id="element-2732">Improve cash flow by paying suppliers early</li>
                    <li data-id="element-2733">Maintain good supplier relationships</li>
                    <li data-id="element-2734">Take advantage of early payment discounts</li>
                    <li data-id="element-2735">Flexible repayment terms</li>
                  </ul>
                </div>
              </div>
            </div>
            <div className="flex justify-end" data-id="element-2736">
              <button onClick={() => setStep(2)} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700" data-id="element-2737">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 2: Select Provider */}
        {step === 2 && <div className="space-y-6" data-id="element-2738">
            <div data-id="element-2739">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2740">
                Select DFCC Bank Financing Package
              </h2>
              <p className="text-gray-600" data-id="element-2741">
                Choose a financing package that best suits your needs
              </p>
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6" data-id="element-2742">
              <div className="flex items-start gap-3" data-id="element-2743">
                <BuildingIcon size={20} className="text-blue-600 mt-0.5" data-id="element-2744" />
                <div data-id="element-2745">
                  <div className="font-semibold text-blue-900 mb-1" data-id="element-2746">
                    DFCC Bank - Your Trusted Financial Partner
                  </div>
                  <div className="text-sm text-blue-800" data-id="element-2747">
                    As a valued DFCC Bank customer, you have access to exclusive
                    financing packages tailored for your business needs.
                  </div>
                </div>
              </div>
            </div>
            <div className="space-y-4" data-id="element-2748">
              {dfccLoanPackages.map(pkg => <label key={pkg.id} className={`block p-6 border-2 rounded-lg cursor-pointer transition-all ${formData.loanPackage === pkg.id ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-blue-300'}`} data-id="element-2749">
                  <input type="radio" name="loanPackage" value={pkg.id} checked={formData.loanPackage === pkg.id} onChange={e => setFormData({
              ...formData,
              loanPackage: e.target.value
            })} className="sr-only" data-id="element-2750" />
                  <div className="flex items-start justify-between" data-id="element-2751">
                    <div className="flex-1" data-id="element-2752">
                      <div className="flex items-center gap-2 mb-2" data-id="element-2753">
                        <div className="text-lg font-semibold text-gray-900" data-id="element-2754">
                          {pkg.name}
                        </div>
                        {formData.loanPackage === pkg.id && <CheckCircleIcon size={20} className="text-blue-600" data-id="element-2755" />}
                      </div>
                      <p className="text-sm text-gray-600 mb-4" data-id="element-2756">
                        {pkg.description}
                      </p>
                      <div className="grid grid-cols-3 gap-4 text-sm" data-id="element-2757">
                        <div data-id="element-2758">
                          <div className="text-gray-500" data-id="element-2759">Interest Rate</div>
                          <div className="font-medium text-gray-900" data-id="element-2760">
                            {pkg.interestRate}
                          </div>
                        </div>
                        <div data-id="element-2761">
                          <div className="text-gray-500" data-id="element-2762">Max Term</div>
                          <div className="font-medium text-gray-900" data-id="element-2763">
                            {pkg.maxTerm}
                          </div>
                        </div>
                        <div data-id="element-2764">
                          <div className="text-gray-500" data-id="element-2765">Processing Time</div>
                          <div className="font-medium text-gray-900" data-id="element-2766">
                            {pkg.processingTime}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </label>)}
            </div>
            <div className="flex justify-between" data-id="element-2767">
              <button onClick={() => setStep(1)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2768">
                Back
              </button>
              <button onClick={() => setStep(3)} disabled={!formData.loanPackage} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2769">
                Continue
              </button>
            </div>
          </div>}
        {/* Step 3: Financing Terms */}
        {step === 3 && <div className="space-y-6" data-id="element-2770">
            <div data-id="element-2771">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2772">
                Financing Terms
              </h2>
              <p className="text-gray-600" data-id="element-2773">
                Specify the financing amount and terms
              </p>
            </div>
            <div className="space-y-6" data-id="element-2774">
              <div data-id="element-2775">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2776">
                  Financing Amount (LKR) *
                </label>
                <input type="number" value={formData.financingAmount} onChange={e => setFormData({
              ...formData,
              financingAmount: e.target.value
            })} placeholder="Enter amount" max={bill.amount} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2777" />
                <div className="mt-1 text-sm text-gray-500" data-id="element-2778">
                  Maximum: Rs. {bill.amount.toLocaleString('en-LK')}
                </div>
              </div>
              <div data-id="element-2779">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2780">
                  Requested Term (Days) *
                </label>
                <select value={formData.requestedTerm} onChange={e => setFormData({
              ...formData,
              requestedTerm: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2781">
                  <option value="15" data-id="element-2782">15 days</option>
                  <option value="30" data-id="element-2783">30 days</option>
                  <option value="45" data-id="element-2784">45 days</option>
                  <option value="60" data-id="element-2785">60 days</option>
                  <option value="90" data-id="element-2786">90 days</option>
                </select>
              </div>
              <div data-id="element-2787">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2788">
                  Purpose of Financing *
                </label>
                <select value={formData.purpose} onChange={e => setFormData({
              ...formData,
              purpose: e.target.value
            })} className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2789">
                  <option value="" data-id="element-2790">Select purpose</option>
                  <option value="early_payment" data-id="element-2791">Early Payment Discount</option>
                  <option value="cash_flow" data-id="element-2792">Cash Flow Management</option>
                  <option value="supplier_relationship" data-id="element-2793">
                    Supplier Relationship
                  </option>
                  <option value="other" data-id="element-2794">Other</option>
                </select>
              </div>
              <div data-id="element-2795">
                <label className="block text-sm font-medium text-gray-700 mb-2" data-id="element-2796">
                  Additional Notes
                </label>
                <textarea value={formData.additionalNotes} onChange={e => setFormData({
              ...formData,
              additionalNotes: e.target.value
            })} rows={4} placeholder="Any additional information..." className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent" data-id="element-2797" />
              </div>
            </div>
            <div className="flex justify-between" data-id="element-2798">
              <button onClick={() => setStep(2)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2799">
                Back
              </button>
              <button onClick={() => setStep(4)} disabled={!formData.financingAmount || !formData.requestedTerm || !formData.purpose} className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-2800">
                Review Request
              </button>
            </div>
          </div>}
        {/* Step 4: Review & Submit */}
        {step === 4 && <div className="space-y-6" data-id="element-2801">
            <div data-id="element-2802">
              <h2 className="text-2xl font-bold text-gray-900 mb-2" data-id="element-2803">
                Review Financing Request
              </h2>
              <p className="text-gray-600" data-id="element-2804">
                Please review your financing request before submitting
              </p>
            </div>
            <div className="bg-gray-50 rounded-lg p-6 space-y-6" data-id="element-2805">
              <div data-id="element-2806">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-2807">
                  BILL INFORMATION
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-2808">
                  <div data-id="element-2809">
                    <div className="text-sm text-gray-500" data-id="element-2810">Bill Number</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2811">
                      {bill.billNumber}
                    </div>
                  </div>
                  <div data-id="element-2812">
                    <div className="text-sm text-gray-500" data-id="element-2813">Supplier</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2814">
                      {bill.supplierName}
                    </div>
                  </div>
                </div>
              </div>
              <div className="pt-4 border-t border-gray-200" data-id="element-2815">
                <h3 className="text-sm font-medium text-gray-500 mb-3" data-id="element-2816">
                  FINANCING DETAILS
                </h3>
                <div className="grid grid-cols-2 gap-4" data-id="element-2817">
                  <div data-id="element-2818">
                    <div className="text-sm text-gray-500" data-id="element-2819">Bank</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2820">
                      DFCC Bank
                    </div>
                  </div>
                  <div data-id="element-2821">
                    <div className="text-sm text-gray-500" data-id="element-2822">Package</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2823">
                      {selectedPackage?.name}
                    </div>
                  </div>
                  <div data-id="element-2824">
                    <div className="text-sm text-gray-500" data-id="element-2825">Interest Rate</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2826">
                      {selectedPackage?.interestRate}
                    </div>
                  </div>
                  <div data-id="element-2827">
                    <div className="text-sm text-gray-500" data-id="element-2828">Processing Time</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2829">
                      {selectedPackage?.processingTime}
                    </div>
                  </div>
                  <div data-id="element-2830">
                    <div className="text-sm text-gray-500" data-id="element-2831">
                      Financing Amount
                    </div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2832">
                      Rs.{' '}
                      {parseFloat(formData.financingAmount).toLocaleString('en-LK')}
                    </div>
                  </div>
                  <div data-id="element-2833">
                    <div className="text-sm text-gray-500" data-id="element-2834">Term</div>
                    <div className="text-base font-medium text-gray-900" data-id="element-2835">
                      {formData.requestedTerm} days
                    </div>
                  </div>
                  <div data-id="element-2836">
                    <div className="text-sm text-gray-500" data-id="element-2837">Purpose</div>
                    <div className="text-base font-medium text-gray-900 capitalize" data-id="element-2838">
                      {formData.purpose.replace('_', ' ')}
                    </div>
                  </div>
                </div>
              </div>
              {formData.additionalNotes && <div className="pt-4 border-t border-gray-200" data-id="element-2839">
                  <h3 className="text-sm font-medium text-gray-500 mb-2" data-id="element-2840">
                    ADDITIONAL NOTES
                  </h3>
                  <p className="text-sm text-gray-700" data-id="element-2841">
                    {formData.additionalNotes}
                  </p>
                </div>}
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4" data-id="element-2842">
              <div className="flex items-start gap-3" data-id="element-2843">
                <FileTextIcon size={20} className="text-blue-600 mt-0.5" data-id="element-2844" />
                <div className="text-sm text-blue-800" data-id="element-2845">
                  By submitting this request, you agree to the financing terms
                  and conditions set by DFCC Bank for the{' '}
                  {selectedPackage?.name}. The request will be processed within{' '}
                  {selectedPackage?.processingTime}.
                </div>
              </div>
            </div>
            <div className="flex justify-between" data-id="element-2846">
              <button onClick={() => setStep(3)} className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50" data-id="element-2847">
                Back
              </button>
              <button onClick={handleSubmit} className="px-6 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700" data-id="element-2848">
                Submit Financing Request
              </button>
            </div>
          </div>}
      </div>
    </div>;
}